/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AuthService } from 'app/um/services/auth-service';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { EventAggregator } from 'aurelia-event-aggregator';
import { MenuRefreshEvent } from 'app/shared/lib/events';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { localSort } from 'app/shared/lib/sorting';
import { I18N } from 'aurelia-i18n';
import { IPopup } from 'app/shared/elements/popup-base';
import { validate } from 'app/shared/elements/ess-rule';
import { deepClone } from 'app/shared/lib/utilities';
import * as UM from 'app/um/models/essette-api';
import { confirmation } from 'app/shared/elements/ess-confirm';

interface CustomPortalQueue extends UM.PortalQueue {
    linesOfBusinessDisplay?: string | null;
    activeLinesOfBusiness?: string[] | null;
    inactiveLinesOfBusiness?: string[] | null;

    healthPlansDisplay?: string | null;
    activeHealthPlans?: string[] | null;
    inactiveHealthPlans?: string[] | null;

    authSubClassesDisplay?: string | null;
    activeAuthSubClasses?: string[] | null;
    inactiveAuthSubClasses?: string[] | null;
}

@autoinject()
export class ManagePortalQueuesPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected portalQueues: CustomPortalQueue[] = [];
    protected editedPortalQueue: UM.PortalQueue = {};

    protected validate = validate;
    protected isValid: boolean = false;

    private apiSorting = new Sorting();
    private localSorting = new Sorting();
    private localSortProperties: string[] = ['linesOfBusinessDisplay', 'healthPlansDisplay', 'authSubClassesDisplay'];

    protected resultCount = 0;
    protected pageSize = 25;
    protected includeInactive = false;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;
    protected $form!: IForm;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        private readonly events: EventAggregator,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected async activate(): Promise<void> {
        this.reload();
    }

    ////****************** Events ***************************************************////
    public async reload(): Promise<void> {
        await this.loadPortalQueues();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;

        //If LocalSort
        if(this.localSortProperties.indexOf(property) > -1) {
            const { localSorting } = this;
            const portalQueues: CustomPortalQueue[] = <CustomPortalQueue[]>this.portalQueues;
            if(direction === null) {
                this.localSorting.remove(property);
                if(this.localSorting.entries.length == 0) {
                    this.reload();
                };
            } else {
                this.localSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            localSort(portalQueues, localSorting);
        }
        // Else If API Sort
        else {
            const { apiSorting } = this;
            if(direction === null) {
                apiSorting.remove(property);
            } else {
                apiSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            this.reload();
        }
    }

    protected handleFormSuccess(): void {
        this.closeModal();
        this.refreshMainMenu();
        this.reload();
    }

    ////****************** Functions ************************************************////
    protected refreshMainMenu(): void {
        this.events.publish(new MenuRefreshEvent);
        this.accountContext.clear();
    }

    protected closeModal(): void {
        if(this.$form.reset) this.$form.reset();
        this.$modal.close();
    }

    protected savePortalQueue(portalQueue: UM.PortalQueue): void {
        this.editedPortalQueue = deepClone(portalQueue);
        this.$modal.open();
    }

    // Set Custom Portal Queue Details
    private setPortalQueueDetails(portalQueues: CustomPortalQueue[]): void {
        portalQueues?.forEach(queue => {

            // Configure Lines of Business column data
            if(queue.flag?.allowAnyLineOfBusiness) {
                // if AnyLineOfBusiness flag enabled, display "Any Line of Business"
                queue.linesOfBusinessDisplay = this.i18n.tr('ui:labels.any', { what: this.i18n.tr('cd:lob.$') });
            } else if(queue?.linesOfBusiness && queue.linesOfBusiness.length > 0) {
                queue.activeLinesOfBusiness = queue.linesOfBusiness
                    .filter(lob => lob.isActive)
                    .flatMap(lob => lob.description ?? '')
                    .sort((a, b) => a.localeCompare(b));
                queue.inactiveLinesOfBusiness = queue.linesOfBusiness
                    .filter(lob => !lob.isActive)
                    .flatMap(lob => lob.description ?? '')
                    .sort((a, b) => a.localeCompare(b));

                if(!queue.activeLinesOfBusiness.length && !queue.inactiveLinesOfBusiness.length) {
                    // if no active lob and no inactive lob, display nothing at all
                    queue.linesOfBusinessDisplay = '';
                } else if(queue.activeLinesOfBusiness.length === 1) {
                    // if only one active lob, display only that lob
                    queue.linesOfBusinessDisplay = queue.activeLinesOfBusiness[0];
                } else if(queue.activeLinesOfBusiness.length > 1) {
                    // if more than one active lob, display "n Lines of Business"
                    queue.linesOfBusinessDisplay = `${queue.activeLinesOfBusiness.length} ` + this.i18n.tr('cd:lob.$', { count: 2 });
                } else if(!queue.activeLinesOfBusiness.length && queue.inactiveLinesOfBusiness.length > 0) {
                    // if no active lob and more than zero inactive lobs, display "None"
                    queue.linesOfBusinessDisplay = this.i18n.tr('ui:labels.none');
                }
            }

            // Configure Health Plans column data
            if(queue.flag?.allowAnyHealthPlan) {
                // if AnyHealthPlan flag enabled, display "Any Health Plan"
                queue.healthPlansDisplay = this.i18n.tr('ui:labels.any', { what: this.i18n.tr('cd:healthPlan.$') });
            } else if(queue?.healthPlans && queue.healthPlans.length > 0) {
                queue.activeHealthPlans = queue.healthPlans
                    .filter(hp => hp.isActive)
                    .flatMap(hp => hp.description ?? '')
                    .sort((a, b) => a.localeCompare(b));
                queue.inactiveHealthPlans = queue.healthPlans
                    .filter(hp => !hp.isActive)
                    .flatMap(hp => hp.description ?? '')
                    .sort((a, b) => a.localeCompare(b));

                if(!queue.activeHealthPlans.length && !queue.inactiveHealthPlans.length) {
                    // if no active health plan and no inactive health plan, display nothing at all
                    queue.healthPlansDisplay = '';
                } else if(queue.activeHealthPlans.length === 1) {
                    // if only one active health plan, display only that health plan
                    queue.healthPlansDisplay = queue.activeHealthPlans[0];
                } else if(queue.activeHealthPlans.length > 1) {
                    // if more than one active health plan, display "n Health Plans"
                    queue.healthPlansDisplay = `${queue.activeHealthPlans.length} ` + this.i18n.tr('cd:healthPlan.$', { count: 2 });
                } else if(!queue.activeHealthPlans.length && queue.inactiveHealthPlans.length > 0) {
                    // if no active health plan and more than zero inactive health plans, display "None"
                    queue.healthPlansDisplay = this.i18n.tr('ui:labels.none');
                }
            }

            // Configure Auth Sub Classes column data
            if(queue.flag?.allowAnyAuthSubClass) {
                // if AnyAuthSubClass flag enabled, display "Any Auth Sub Class"
                queue.authSubClassesDisplay = this.i18n.tr('ui:labels.any', { what: this.i18n.tr('um:auth.class.subClass.$long') });
            } else if(queue?.authSubClasses && queue.authSubClasses.length > 0) {
                queue.activeAuthSubClasses = queue.authSubClasses
                    .filter(sc => sc.isActive)
                    .flatMap(sc => sc.description ?? '')
                    .sort((a, b) => a.localeCompare(b));
                queue.inactiveAuthSubClasses = queue.authSubClasses
                    .filter(sc => !sc.isActive)
                    .flatMap(sc => sc.description ?? '')
                    .sort((a, b) => a.localeCompare(b));

                if(!queue.activeAuthSubClasses.length && !queue.inactiveAuthSubClasses.length) {
                    // if no active auth sub class and no inactive auth sub class, display nothing at all
                    queue.authSubClassesDisplay = '';
                } else if(queue.activeAuthSubClasses.length === 1) {
                    // if only one active auth sub class, display only that auth sub class
                    queue.authSubClassesDisplay = queue.activeAuthSubClasses[0];
                } else if(queue.activeAuthSubClasses.length > 1) {
                    // if more than one active auth sub class, display "n Auth Sub Classes"
                    queue.authSubClassesDisplay = `${queue.activeAuthSubClasses.length} ` + this.i18n.tr('um:auth.class.subClass.$long', { count: 2 });
                } else if(!queue.activeAuthSubClasses.length && queue.inactiveAuthSubClasses.length > 0) {
                    // if no active auth sub classes and more than zero inactive auth sub classes, display "None"
                    queue.authSubClassesDisplay = this.i18n.tr('ui:labels.none');
                }
            }
        });
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadPortalQueues(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.apiSorting,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();

            const includeInactiveDetails = true;
            const response = await this.service.loadPortalQueues(options, [], [], includeInactiveDetails);
            this.setPortalQueueDetails(<CustomPortalQueue[]>response.results);
            this.portalQueues = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async deactivatePortalQueue(portalQueue: UM.PortalQueue): Promise<void> {
        if(!portalQueue.id) return;

        const result = await confirmation.showDeactivate('um:portalQueue.$', 1, false);
        if(result.dialogResponse === confirmation.options.CANCEL) { return; }

        if(!(result.dialogResponse === confirmation.options.OK)) {
            return;
        }

        let isSuccessful = false;

        try {
            this.state.beginSave();
            const response = await this.service.activatePortalQueue(portalQueue.id, !portalQueue.isActive);
            isSuccessful = response.isSuccessful;

            this.refreshMainMenu();
            this.reload();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.activate('um:portalQueue.$', false, isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async reactivatePortalQueue(portalQueue: UM.PortalQueue): Promise<void> {
        if(!portalQueue.id) return;
        let isSuccessful = false;

        try {
            this.state.beginSave();
            const response = await this.service.activatePortalQueue(portalQueue.id, !portalQueue.isActive);
            isSuccessful = response.isSuccessful;

            this.refreshMainMenu();
            this.reload();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.activate('um:portalQueue.$', true, isSuccessful);
            this.state.endSave();
        }
    }
}
